import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getSubproductLots, setSubproductLotRemaining, getLossReasons, fmt } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import { Recycle, PackageX, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const unitLabel = (u) => (u === "l" ? "L" : "kg");
const BUILTIN_REASONS = ["expired", "damaged", "quality", "other"];

// Adjust a by-product lot's remaining stock (e.g. whey thrown away -> set to 0).
function AdjustDialog({ lot, onClose, onSaved }) {
  const { t } = useT();
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [customReasons, setCustomReasons] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lot) { setValue(String(lot.remaining ?? "")); setReason(""); setNote(""); }
  }, [lot]);
  useEffect(() => { getLossReasons().then((d) => setCustomReasons(d.custom || [])).catch(() => {}); }, []);

  if (!lot) return null;
  const qty = Number(lot.quantity || 0);
  const parsed = value === "" ? null : Number(value);
  const invalid = parsed === null || Number.isNaN(parsed) || parsed < 0 || parsed > qty;

  const save = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      await setSubproductLotRemaining(lot.id, { remaining: parsed, reason: reason || null, note });
      toast.success(t("subproducts.adjusted"));
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : t("subproducts.adjustError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="subproduct-adjust-dialog">
        <DialogHeader>
          <DialogTitle>{t("subproducts.adjustTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-secondary/50 p-3 text-sm">
            <span className="font-mono text-xs">{lot.lot_code || "-"}</span> · <span className="font-medium">{lot.name}</span>
            <div className="mt-1 text-xs text-muted-foreground">
              {t("subproducts.remaining")}: <span className="font-mono">{fmt(lot.remaining, 2)} / {fmt(qty, 2)} {unitLabel(lot.unit)}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("subproducts.newRemaining")} ({unitLabel(lot.unit)})</Label>
            <div className="flex gap-2">
              <Input type="number" min="0" max={qty} step="0.01" value={value}
                onChange={(e) => setValue(e.target.value)}
                className={invalid && value !== "" ? "border-destructive" : ""}
                data-testid="subproduct-adjust-input" />
              <Button type="button" variant="outline" onClick={() => setValue("0")} data-testid="subproduct-adjust-zero">
                {t("subproducts.setZero")}
              </Button>
            </div>
            {invalid && value !== "" && (
              <p className="text-xs text-destructive">{t("subproducts.rangeHint", { max: fmt(qty, 2) })}</p>
            )}
          </div>
          {parsed !== null && !invalid && parsed < Number(lot.remaining || 0) && (
            <div className="space-y-1.5">
              <Label>{t("subproducts.reasonLabel")}</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="subproduct-adjust-reason"><SelectValue placeholder={t("subproducts.reasonPlaceholder")} /></SelectTrigger>
                <SelectContent>
                  {BUILTIN_REASONS.map((r) => <SelectItem key={r} value={r}>{t(`stock.reason.${r}`)}</SelectItem>)}
                  {customReasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t("subproducts.noteLabel")}</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="subproduct-adjust-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="subproduct-adjust-cancel">{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving || invalid} data-testid="subproduct-adjust-save">
            {saving ? "..." : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Subproducts() {
  const { t } = useT();
  const [lots, setLots] = useState(null);
  const [availableOnly, setAvailableOnly] = useState(true);
  const [nameFilter, setNameFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [adjustLot, setAdjustLot] = useState(null);

  const load = async () => {
    setLots(null);
    try { setLots(await getSubproductLots({ availableOnly })); }
    catch { toast.error(t("subproducts.loadError")); setLots([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [availableOnly]);

  const names = useMemo(() => Array.from(new Set((lots || []).map((l) => l.name).filter(Boolean))).sort(), [lots]);

  const filtered = useMemo(() => {
    return (lots || []).filter((l) => {
      if (nameFilter !== "all" && l.name !== nameFilter) return false;
      const d = (l.date || "").slice(0, 10);
      if (from && d && d < from) return false;
      if (to && d && d > to) return false;
      return true;
    });
  }, [lots, nameFilter, from, to]);

  // Totals per by-product name (remaining), respecting the current filter.
  const totals = useMemo(() => {
    const m = {};
    for (const l of filtered) {
      const key = `${l.name}__${l.unit || "kg"}`;
      m[key] = (m[key] || 0) + Number(l.remaining || 0);
    }
    return Object.entries(m).map(([k, v]) => {
      const [name, unit] = k.split("__");
      return { name, unit, remaining: v };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [filtered]);

  const resetFilters = () => { setNameFilter("all"); setFrom(""); setTo(""); };
  const hasFilters = nameFilter !== "all" || from || to;

  return (
    <Layout>
      <div className="space-y-6" data-testid="subproducts-page">
        <div className="flex flex-col gap-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            <Recycle className="h-5 w-5 text-[hsl(var(--brand))]" /> {t("subproducts.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("subproducts.subtitle")}</p>
        </div>

        {/* Totals overview */}
        {totals.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="subproducts-totals">
            {totals.map((tt) => (
              <Card key={`${tt.name}-${tt.unit}`}>
                <CardContent className="p-4">
                  <div className="truncate text-sm font-medium">{tt.name}</div>
                  <div className="mt-1 font-mono text-2xl font-semibold tabular-nums">
                    {fmt(tt.remaining, 2)} <span className="text-sm text-muted-foreground">{unitLabel(tt.unit)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">{t("subproducts.lotsTitle")}</CardTitle>
            <div className="flex items-center gap-2">
              <Label htmlFor="avail-toggle" className="text-xs text-muted-foreground">{t("subproducts.inStockOnly")}</Label>
              <Switch id="avail-toggle" checked={availableOnly} onCheckedChange={setAvailableOnly} data-testid="subproducts-instock-toggle" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("subproducts.filterName")}</Label>
                <Select value={nameFilter} onValueChange={setNameFilter}>
                  <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="subproducts-name-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("subproducts.allNames")}</SelectItem>
                    {names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("subproducts.dateFrom")}</Label>
                <Input type="date" className="h-9 w-[150px]" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="subproducts-date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("subproducts.dateTo")}</Label>
                <Input type="date" className="h-9 w-[150px]" value={to} onChange={(e) => setTo(e.target.value)} data-testid="subproducts-date-to" />
              </div>
              {hasFilters ? (
                <button className="h-9 text-sm text-[hsl(var(--brand))] underline-offset-2 hover:underline" onClick={resetFilters} data-testid="subproducts-reset-filters">
                  {t("subproducts.reset")}
                </button>
              ) : null}
            </div>

            {lots === null ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <PackageX className="h-10 w-10 text-muted-foreground/50" />
                <p className="mt-3 text-sm text-muted-foreground">{t("subproducts.empty")}</p>
              </div>
            ) : (
              <div className="overflow-x-auto thin-scroll">
                <Table data-testid="subproducts-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("subproducts.lotCode")}</TableHead>
                      <TableHead>{t("subproducts.name")}</TableHead>
                      <TableHead>{t("subproducts.source")}</TableHead>
                      <TableHead>{t("subproducts.date")}</TableHead>
                      <TableHead className="text-right">{t("subproducts.quantity")}</TableHead>
                      <TableHead className="text-right">{t("subproducts.remaining")}</TableHead>
                      <TableHead className="w-[64px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((l) => (
                      <TableRow key={l.id} data-testid={`subproduct-row-${l.id}`}>
                        <TableCell className="font-mono text-xs">{l.lot_code || "-"}</TableCell>
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{l.source_production_code || "-"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{l.date ? formatDate(l.date) : "-"}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{fmt(l.quantity, 2)} {unitLabel(l.unit)}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono tabular-nums">{fmt(l.remaining, 2)} {unitLabel(l.unit)}</span>
                          {Number(l.remaining) <= 0 && <Badge className="badge-bad ml-2 text-[10px]">{t("subproducts.depleted")}</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="h-8 w-8" title={t("subproducts.adjust")}
                            onClick={() => setAdjustLot(l)} data-testid={`subproduct-adjust-btn-${l.id}`}>
                            <SlidersHorizontal className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        <AdjustDialog lot={adjustLot} onClose={() => setAdjustLot(null)}
          onSaved={() => { setAdjustLot(null); load(); }} />
      </div>
    </Layout>
  );
}
